import { computed, onBeforeUnmount, Ref, ref, watch } from 'vue';
import { Task, useTask } from '@/ui/concerns';
import { EventBus } from '@/ui/EventBus';
import { useCurrentRoute } from '@/ui/routes';
import { Events } from '@/ui/events';
import { v4 as uuidv4 } from 'uuid';
import { toasted } from '@/ui/helpers';
import { t } from '@/ui/i18n/i18n';

type BaseEntity<T> = (T & { id: number });

const defaultMapIds = <T>(record: BaseEntity<T>) => record.id;
const defaultOnDeleteFilterFn = <T extends {id: number}>(deleteEntity: BaseEntity<T>) =>
  (currentElement: T) => currentElement.id !== deleteEntity.id;

interface UseBulkDeletableArgs<T> {
  getList: Task;
  bulkDeleteFn: Function;
  onDeletion?: Function;
  mapIds?: (value: T, index?: number, array?: T[]) => number;
  onDeleteFilterFn?: (deleteEntity: BaseEntity<T>) => ((currentElement: T) => boolean);
}

export function useBulkDeletable<T extends { id: number; authorizedTo?: { delete: boolean } }>({
  getList,
  bulkDeleteFn,
  onDeletion,
  mapIds = defaultMapIds,
  onDeleteFilterFn = defaultOnDeleteFilterFn,
}: UseBulkDeletableArgs<T>) {
  const selectedResources = ref<T[]>([]) as Ref<T[]>;

  const currentRoute = useCurrentRoute();

  const clearSelectedResources = () => { selectedResources.value = []; };

  const deleteBulkTask = useTask(
    () => bulkDeleteFn(selectedResources.value.map(mapIds)),
    () => {
      selectedResources.value = [];
      EventBus.$emit(Events.recordsDeleted);
      getList.run();
      onDeletion && onDeletion();
    },
    uuidv4(),
    error => {
      toasted.error(t(error.message));
    },
  );

  const onCheckedRowsUpdate = (resources: T[]) => {
    selectedResources.value = resources;
  };

  const onDeleteSingleResource = ({ entity }: {entity: T}) => {
    selectedResources.value = selectedResources.value.filter(onDeleteFilterFn(entity as BaseEntity<T>));
  };

  EventBus.$on(Events.bulkDeleteClickConfirmation, deleteBulkTask.run);
  EventBus.$on(Events.singleRecordDeleted, onDeleteSingleResource);

  onBeforeUnmount(() => {
    EventBus.$off(Events.bulkDeleteClickConfirmation, deleteBulkTask.run);
    EventBus.$off(Events.singleRecordDeleted, onDeleteSingleResource);
  });

  watch(currentRoute, clearSelectedResources);

  const isBulkDeletionDisabled = computed(
    () => selectedResources.value.some(resource => !resource.authorizedTo?.delete),
  );

  return {
    selectedResources,
    deleteBulkTask,
    onCheckedRowsUpdate,
    isBulkDeletionDisabled,
  };
}
